import { createTranslator, flattenCatalog, pseudoLocalizeCatalog } from '../_helpers/i18n.js';

/**
 * Locale registry (ENG-387). `src/locales/<tag>/common.json` holds one
 * catalog per BCP 47 tag; this file says which of them a user may switch to.
 *
 * A locale is only selectable once `enabled` is true, and the catalog gate in
 * `catalog.test.js` fails the build if an enabled locale is missing a key, an
 * interpolation variable or a plural form the English source has — so the
 * flag cannot be flipped ahead of the catalog. Keep `enabled: false` while a
 * translation is in progress.
 *
 * ENG-376 owns the supported-locale list and the pilot language; until it
 * lands, en-US is the only real locale and the pseudo-locales below stand in
 * for "a long translation" and "a right-to-left translation".
 */
export const DEFAULT_LOCALE = 'en-US';

export const LOCALES = Object.freeze({
  'en-US': Object.freeze({ tag: 'en-US', language: 'en', dir: 'ltr', nativeName: 'English (US)', enabled: true }),
});

/**
 * Every `<tag>/common.json` beside this file, keyed by tag (ENG-378). Vite
 * resolves the glob at build time and bundles the catalogs eagerly — each is
 * small, and the translator reads them synchronously — so adding a locale is
 * a directory plus a `LOCALES` entry; the catalog gate checks the two agree.
 */
const CATALOGS = Object.freeze(
  Object.fromEntries(
    Object.entries(import.meta.glob('./*/common.json', { eager: true, import: 'default' })).map(([path, catalog]) => [
      path.split('/')[1],
      catalog,
    ])
  )
);

/**
 * Derived from English at runtime, never checked in, and only offered
 * outside production (or with VITE_ENABLE_PSEUDO_LOCALES=true on a staging
 * build). `en-XA` accents every letter and pads ~35% (hard-coded strings and
 * clipping); `ar-XB` forces right-to-left rendering (mirroring). Both keep
 * English plural rules because the text is still English.
 */
export const PSEUDO_LOCALES = Object.freeze({
  'en-XA': Object.freeze({
    tag: 'en-XA',
    language: 'en',
    dir: 'ltr',
    nativeName: 'Pseudo-locale (expanded text)',
    pseudo: 'expand',
    pluralLocale: 'en-US',
  }),
  'ar-XB': Object.freeze({
    tag: 'ar-XB',
    language: 'ar',
    dir: 'rtl',
    nativeName: 'Pseudo-locale (right-to-left)',
    pseudo: 'rtl',
    pluralLocale: 'en-US',
  }),
});

export const pseudoLocalesAllowed = () =>
  !import.meta.env.PROD || import.meta.env.VITE_ENABLE_PSEUDO_LOCALES === 'true';

/** Registry entry for a canonical tag, or undefined when unknown / not offered here. */
export const getLocaleMeta = (tag) => LOCALES[tag] ?? (pseudoLocalesAllowed() ? PSEUDO_LOCALES[tag] : undefined);

/** Whether a user may switch to this tag right now. */
export const isActivatable = (tag) => {
  const meta = getLocaleMeta(tag);
  return Boolean(meta && (meta.pseudo || meta.enabled));
};

/** What the language selector offers, in display order. */
export const selectableLocales = () => [
  ...Object.values(LOCALES).filter((meta) => meta.enabled),
  ...(pseudoLocalesAllowed() ? Object.values(PSEUDO_LOCALES) : []),
];

/** `en_us` / ` EN-us ` → `en-US`; anything that is not a BCP 47 tag → null. */
export const canonicalizeLocale = (tag) => {
  if (typeof tag !== 'string' || !tag.trim()) return null;
  try {
    return Intl.getCanonicalLocales(tag.trim().replace(/_/g, '-'))[0] ?? null;
  } catch {
    return null;
  }
};

/**
 * First activatable candidate, by exact tag and then by language
 * (`en-GB` → `en-US`); invalid, unknown and disabled tags are skipped and
 * the default wins when nothing matches.
 */
export const resolveLocale = (candidates = []) => {
  for (const candidate of candidates) {
    const tag = canonicalizeLocale(candidate);
    if (!tag) continue;
    if (isActivatable(tag)) return tag;
    const language = tag.split('-')[0];
    const sameLanguage = selectableLocales().find((meta) => !meta.pseudo && meta.language === language);
    if (sameLanguage) return sameLanguage.tag;
  }
  return DEFAULT_LOCALE;
};

export const directionOf = (tag) => getLocaleMeta(tag)?.dir ?? 'ltr';
export const isRtl = (tag) => directionOf(tag) === 'rtl';
/** The language subtag of a locale (`en-US` → `en`), for `lang=` marks on text in another language than the page's. */
export const languageOf = (tag) => getLocaleMeta(tag)?.language ?? String(tag ?? '').split('-')[0].toLowerCase();
export const pluralLocaleOf = (tag) => getLocaleMeta(tag)?.pluralLocale ?? tag;

const flatCache = new Map();

/** Flat `{ key: message }` catalog for a tag; pseudo-locales are derived from English on first use. */
export const getMessages = (tag) => {
  if (flatCache.has(tag)) return flatCache.get(tag);
  let messages = {};
  if (CATALOGS[tag]) {
    messages = flattenCatalog(CATALOGS[tag]);
  } else if (PSEUDO_LOCALES[tag]) {
    messages = pseudoLocalizeCatalog(flattenCatalog(CATALOGS[DEFAULT_LOCALE]), { rtl: PSEUDO_LOCALES[tag].pseudo === 'rtl' });
  }
  flatCache.set(tag, messages);
  return messages;
};

/** Raw nested catalogs as bundled, keyed by tag (gates compare these against the files on disk). */
export const bundledCatalogs = () => CATALOGS;

/**
 * `t()` bound to the English source catalog, for helpers that are called
 * without a component's `t` (tests, non-React call sites). Components and
 * hooks take `t` from `useT()` / `useLocale()` instead so the active locale
 * applies; helpers that produce user-visible text should accept `t` as a
 * parameter and default to this.
 */
export const englishT = createTranslator({
  locale: DEFAULT_LOCALE,
  messages: getMessages(DEFAULT_LOCALE),
  fallbackMessages: getMessages(DEFAULT_LOCALE),
});
